<template>
  <div class="gmf-card-stack-wrapper"
    :style="{
      width: containerWidth,
    }"
  >
    <div
      class="gmf-card-stack"
      ref="stack"
      :style="{
        width: stackWidth,
        height: maxCardHeight,
      }"
    >
      <div
        class="gmf-card-stack-card"
        v-for="(card, index) in stack"
        :key="card._id"
        :style="{
          display: card.display,
          opacity: card.opacity,
          top: `${card.yPos}px`,
          width: `${card.width}px`,
          zIndex: card.zIndex || 100,
          transition: `
            transform ${
              isDragging ? 0 : speed
            }s ease,
            opacity ${
              isDragging ? 0 : speed
            }s ease
          `,
          transform: `
            scale(${card.scale}, ${card.scale}) 
            translate(${card.xPos}px, 0)
            rotate(${card.rotation}deg)
          `,
        }"
      >
        <slot v-bind:card="{ ...card, $index: index }" name="card"></slot>
      </div>

      <slot
        name="nav"
        v-bind="{ activeCardIndex: originalActiveCardIndex, next, previous }"
      ></slot>
    </div>
  </div>
</template>

<style scoped>
.gmf-card-stack-wrapper {
  position: relative;
  max-width: 100%;
  display: flex;
  justify-content: center;
}

.gmf-card-stack {
  position: relative;
  overflow: hidden;
  max-width: 100%;
}

.gmf-card-stack-card {
  position: absolute;
  transform-origin: right;
  cursor: grab;
}
</style>

<script>
import debounce from '@/utils/debounce';

export default {
  name: "VueCardStack",
  props: {
    cards: {
      type: Array,
      default: () => [],
    },
    // Current card index.
    modelValue: {
      type: Number,
      default: () => 0,
    },
    containerWidth: {
      type: String,
      default: () => '100%',
    },
    stackWidth: {
      type: String,
      default: () => "400px",
    },
    cardWidth: {
      type: Number,
      default: () => 300,
    },
    sensitivity: {
      type: Number,
      default: () => 0.25,
    },
    maxVisibleCards: {
      type: Number,
      default: () => 3,
    },
    scaleMultiplier: {
      type: Number,
      default: () => 0.5, // last visible card will be 50% scale
    },
    /**
     * The length of the transition when a card is swiped, in seconds.
     */
    speed: {
      type: Number,
      default: () => 0.2,
    },
    cardGap: {
      type: Number,
      default: () => 16,
    },
    paddingVertical: {
      type: Number,
      default: () => 10,
    },
  },
  data() {
    return {
      stack: [],
      internalCards: [...this.cards],
      removedCards: [],
      activeCardIndex: 1,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      isDraggingRight: false,
      cardToMove: null,
      width: 0, // Used in a recompute hack for resizing events.
      maxCardHeight: 'auto',
    };
  },
  mounted() {
    this.init();
    window.addEventListener("resize", this.debounceHandleResize);
    this.$el.addEventListener(this.touchStartEvent, this.onTouchStart);
    document.addEventListener(this.touchEndEvent, this.onTouchEnd);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.debounceHandleResize);
    this.$el.removeEventListener(this.touchStartEvent, this.onTouchStart);
    document.removeEventListener(this.touchEndEvent, this.onTouchEnd);
  },
  computed: {
    _scaleMultiplier() {
      return ((this.scaleMultiplier - 1) * -1) / 10;
    },
    _stackWidth () {
      return this.width || this.$refs.stack.clientWidth;
    },
    elementXPosOffset() {
      return this.$el.getBoundingClientRect().x;
    },
    isTouch() {
      return "ontouchstart" in window;
    },
    dragEvent() {
      return this.isTouch ? "touchmove" : "mousemove";
    },
    touchStartEvent() {
      return this.isTouch ? "touchstart" : "mousedown";
    },
    touchEndEvent() {
      return this.isTouch ? "touchend" : "mouseup";
    },
    xPosOffset () {
      // Left side of the top card (index 1), positioned in the center of the container.
      return (this._stackWidth - this.cardWidth) / 2;
    },
    cardRestPoints () {
      return this.internalCards.map((_, index) => {
        if (index === 0) {
          return -this.cardWidth - 10;
        }

        return this.xPosOffset + (index - 1) * this.cardGap;
      });
    },
    cardDefaults () {
      const cardRestPoints = this.cardRestPoints;
      const maxVisibleCards = this.getMaxVisibleCards();

      return this.internalCards.map((_, index) => {
        return {
          opacity: index > 0 && index <= maxVisibleCards ? 1 : 0,
          display: 'block',
          xPos: cardRestPoints[index],
          yPos: this.paddingVertical,
          scale: this.getCardScale(index),
          width: this.cardWidth,
          height: this.cardHeight,
          zIndex: this.internalCards.length - index,
          isDragging: this.isDragging,
          rotation: index === 0 && (! this.isDragging) ? -10 : 0,
        };
      });
    },
    originalActiveCardIndex () {
      if (this.stack[1]) {
        return this.stack[1]._index;
      }

      return 0;
    },
    debounceHandleResize () {
      return debounce(this.handleResize, 250);
    },
  },
  methods: {
    init () {
      // Assign numerical index to each card (original order).
      for (let i = 0; i < this.internalCards.length; i++) {
        this.internalCards[i]._index = i;
      }

      // Create a temp on top of stack (positioned offscreen).
      const tempTopCard = JSON.parse(JSON.stringify(this.internalCards[this.internalCards.length - 1]));
      tempTopCard._id = new Date().getTime();
      this.internalCards.unshift(tempTopCard);
      
      this.stack = this.internalCards.map((card, index) => {
        return {
          _id: new Date().getTime() + index,
          // Assign origianl index to each card.
          _index: card._index,
          raw: card,
          ...this.cardDefaults[index],
        };
      });

      this.updateMaxCardHeight();
    },
    rebuild () {
      this.updateMaxCardHeight();
      this.$nextTick(() => {
        this.stack = this.stack.map((card, index) => {
          return {
            ...card,
            ...this.cardDefaults[index],
          };
        });
      });
    },
    next () {
      if (this.cards.length === 1) {
        return;
      }

      if (this.stack.length > 2) {
        // Remove the first card in the stack.
        this.removedCards.push(this.stack.shift());
      }
      
      this.rebuild();

      this.$nextTick(() => {
        this.$emit('update:modelValue', this.originalActiveCardIndex);
      });
    },
    previous () {
      if (this.cards.length === 1) {
        return;
      }

      // Remove the last card in the stack.
      if (this.removedCards.length > 0) {
        this.stack.unshift(this.removedCards.pop());
      }

      this.rebuild();
      this.$nextTick(() => {
        this.$emit('update:modelValue', this.originalActiveCardIndex);
      });
    },
    updateStack () {
      
      const activeCard = this.stack[this.activeCardIndex];
      const activeCardRestPoint = this.cardRestPoints[this.activeCardIndex];
      const distanceTravelled = activeCard.xPos - activeCardRestPoint;
      const minDistanceToTravel =
      (this.cardWidth + this.xPosOffset) / (1 / this.sensitivity);

      if (! this.canDrag(distanceTravelled)) {
        return;
      }

      this.$emit("move", 0);

      if (this.isDraggingRight) {
        if (distanceTravelled > minDistanceToTravel) {
          this.previous();
        } else {
          this.rebuild();
        }
      } else {
        if (distanceTravelled * -1 > minDistanceToTravel) {
          this.next();
        } else {
          this.rebuild();
        }
      }
    },
    moveStack (dragXPos) {
      // Drag distance from start position.
      let dragDistance = dragXPos - this.dragStartX;
      
      if (! this.canDrag(dragDistance)) {
        return;
      }

      const maxDragDistance = this.xPosOffset + this.cardWidth;

      // Cap the drag distance.
      if (dragDistance < -maxDragDistance) {
        dragDistance = -maxDragDistance;
      } else if (dragDistance > maxDragDistance) {
        dragDistance = maxDragDistance;
      }

      let rotation = 0;
      let opacity = 1;

      if (this.isDraggingRight) {
        this.activeCardIndex = 0;
        rotation = (1 - dragDistance / maxDragDistance) * -10;
        opacity = dragDistance / maxDragDistance;
      } else {
        this.activeCardIndex = 1; // first card is positioned offscreen
        rotation = dragDistance / maxDragDistance * 10;
        opacity = 1 - (-dragDistance / maxDragDistance);
      }

      this.stack = this.stack.map((card, index) => {
        const isActiveCard = index === this.activeCardIndex;
        const xPos = isActiveCard
          ? this.cardDefaults[index].xPos + dragDistance
          : this.cardDefaults[index].xPos + (dragDistance / maxDragDistance) * this.cardGap;

        const scale = isActiveCard
          ? this.cardDefaults[index].scale
          : this.cardDefaults[index].scale -
            (this._scaleMultiplier /
              (this.cardWidth + this.xPosOffset)) *
              dragDistance;

        return {
          ...card,
          ...this.cardDefaults[index],
          xPos,
          scale,
          opacity: isActiveCard ? opacity : this.cardDefaults[index].opacity,
          rotation: isActiveCard ? rotation : 0,
        };
      });
    },
    getDragXPos (e) {
      return this.isTouch ? e.touches[0].clientX : e.clientX;
    },
    getDragYPos (e) {
      return this.isTouch ? e.touches[0].clientY : e.clientY;
    },
    canDrag (dragDistance) {
      // Only one card left (+ offscreen card). Don't allow dragging left.
      if (this.stack.length === 2 && dragDistance <= 0) {
        return false;
      }
      
      // All cards are visible. Don't allow dragging right.
      if (this.stack.length === this.internalCards.length && dragDistance >= 0) {
        return false;
      }

      return true;
    },
    onTouchStart (e) {
      if (this.cards.length === 1) {
        return;
      }

      this.isDragging = true;
      this.dragStartX = this.getDragXPos(e) - this.elementXPosOffset;
      this.dragStartY = this.getDragYPos(e);

      document.addEventListener(this.dragEvent, this.onDrag);
    },
    onTouchEnd () {
      if (this.cards.length === 1) {
        return;
      }
      
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragStartY = 0;

      document.removeEventListener(this.dragEvent, this.onDrag);
      this.updateStack();
    },
    onDrag (e) {
      const dragXPos = this.getDragXPos(e) - this.elementXPosOffset;

      this.isDraggingRight = dragXPos > this.dragStartX;
      this.moveStack(dragXPos);
    },
    getMaxVisibleCards () {
      let maxVisibleCards = this.internalCards.length > this.maxVisibleCards
        ? this.maxVisibleCards
        : this.internalCards.length;

      return maxVisibleCards;
    },
    getCardScale (index) {
      const scale = index >= 1 ? 1 - this._scaleMultiplier * (index - 1) : 1;
      return scale > 0 ? scale : 0;
    },
    handleResize () {
      this.width = this.$refs.stack.clientWidth;
      this.rebuild();
    },
    updateMaxCardHeight() {
      this.$nextTick(() => {
        let maxHeight = 0;
        this.$refs.stack.querySelectorAll('.gmf-card-stack-card').forEach(card => {
          maxHeight = Math.max(maxHeight, card.offsetHeight);
        });
        this.maxCardHeight = `calc(${ maxHeight + this.paddingVertical * 2 }px)`;
      });
    },
  },
};
</script>